<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="drawer-overlay"
      @click="close"
    ></div>
    <Transition name="drawer">
      <aside v-if="modelValue" class="mobile-drawer">
        <div class="drawer-header">
          <BrandLogo tone="dark" />
          <button type="button" class="drawer-close-btn" @click="close">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <nav class="drawer-nav">
          <ul>
            <li v-for="item in menuItems" :key="item.menuWebCode">
              <a href="#" class="drawer-menu-item" @click.prevent="toggleSubMenu(item)">
                <span>{{ item.name }}</span>
                <i :class="item.isExpanded ? 'fa-solid fa-angle-up' : 'fa-solid fa-angle-down'"></i>
              </a>
              <Transition name="submenu">
                <ul v-if="item.isExpanded" class="drawer-submenu">
                  <li v-for="child in item.childMenuList" :key="child.menuWebCode">
                    <a
                      href="#"
                      :class="{
                        'drawer-submenu-item--active': isActiveChild(child),
                        'drawer-submenu-item--with-badge': submenuBadgeCount(child) > 0
                      }"
                      @click.prevent="navigateTo(child)"
                    >
                      <span>{{ child.name }}</span>
                      <!-- 대기 건수 배지 — 0 이면 그리지 않는다(SURINP-342 D12) -->
                      <span v-if="submenuBadgeCount(child) > 0" class="drawer-submenu-badge">{{ submenuBadgeCount(child) }}</span>
                    </a>
                  </li>
                </ul>
              </Transition>
            </li>
          </ul>
        </nav>
      </aside>
    </Transition>
  </Teleport>
</template>

<script>
import { mapState as mapPiniaState } from 'pinia'
import { useTabStore } from '@/stores/tabs'
import { useMenuStore, resolveMenuPath } from '@/stores/menu'
import { usePartnershipBadgeStore, PARTNERSHIP_BADGE_MENU_CODE } from '@/stores/partnershipBadge'
import BrandLogo from '@/components/common/BrandLogo.vue'

/**
 * @component MobileDrawer
 * @description 모바일 좌측 슬라이드 드로어 메뉴 컴포넌트.
 * v-model(modelValue)로 열림/닫힘 상태를 제어하며, Teleport로 body에 렌더링됩니다.
 * menuStore(공통 메뉴 트리 스토어)를 사용하여 아코디언 방식으로 표시합니다.
 * 탭 전환 시 활성 탭 경로에 해당하는 그룹을 자동 펼치고 sub-menu 를 하이라이트합니다.
 */
export default {
  name: 'MobileDrawer',
  components: { BrandLogo },
  // ── Props ──
  props: {
    /**
     * 드로어 열림 여부 (v-model 바인딩용)
     * @type {boolean}
     */
    modelValue: { type: Boolean, default: false }
  },
  emits: ['update:modelValue'],
  setup() {
    const tabStore = useTabStore()
    const menuStore = useMenuStore()
    const partnershipBadgeStore = usePartnershipBadgeStore()
    return { tabStore, menuStore, partnershipBadgeStore }
  },
  // ── 계산된 속성 ──
  computed: {
    ...mapPiniaState(useTabStore, ['activeTabPath']),
    /**
     * menuStore 의 메뉴 트리를 템플릿에서 사용할 수 있도록 노출합니다.
     */
    menuItems() {
      return this.menuStore.tree
    },
    /**
     * 고객사·협력사 신청 메뉴가 트리에 머지됐는지 여부를 반환합니다.
     * 배지 조회를 이 메뉴가 보이는 사용자에게만 돌리기 위한 게이트입니다.
     * @returns {boolean}
     */
    hasPartnershipMenu() {
      return this.menuStore.tree.some((g) =>
        (g.childMenuList || []).some((c) => c.menuWebCode === PARTNERSHIP_BADGE_MENU_CODE)
      )
    }
  },
  mounted() {
    this.menuStore.loadMenu().then(() => {
      if (this.activeTabPath) {
        this.menuStore.setExpandedByPath(this.activeTabPath)
      }
      // 대기 건수 배지는 폴링하지 않는다 — 메뉴 로드 직후 1회만 조회한다(SURINP-342 D12)
      if (this.hasPartnershipMenu) {
        this.partnershipBadgeStore.fetchCount()
      }
    })
  },
  // ── watch ──
  watch: {
    /**
     * 탭 전환 시 해당 경로의 메뉴 그룹을 자동 펼치고 active 를 갱신합니다.
     */
    activeTabPath(newPath) {
      if (newPath) {
        this.menuStore.setExpandedByPath(newPath)
      }
    }
  },
  // ── 메서드 ──
  methods: {
    /**
     * 상위 메뉴 항목의 서브메뉴 펼침/접힘 상태를 토글합니다. (menuStore 반응성)
     * @param {Object} item - 메뉴 항목 객체
     */
    toggleSubMenu(item) {
      this.menuStore.toggleGroup(item.menuWebCode)
    },
    /**
     * 하위 메뉴가 현재 활성 탭 경로와 일치하는지 판정합니다.
     * @param {Object} child - 하위 메뉴 항목
     * @returns {boolean}
     */
    isActiveChild(child) {
      const activePath = resolveMenuPath(this.activeTabPath)
      return !!activePath && child.urlPath === activePath
    },
    /**
     * 하위 메뉴에 표시할 배지 숫자를 반환합니다. 배지 대상이 아니면 0 입니다.
     * @param {Object} child - 하위 메뉴 항목
     * @returns {number}
     */
    submenuBadgeCount(child) {
      if (child.menuWebCode !== PARTNERSHIP_BADGE_MENU_CODE) return 0
      return this.partnershipBadgeStore.count
    },
    /**
     * 하위 메뉴 클릭 시 해당 경로로 이동하고 드로어를 닫습니다.
     * @param {Object} child - 하위 메뉴 항목 (urlPath 포함)
     */
    navigateTo(child) {
      this.$router.push(child.urlPath)
      this.close()
    },
    /**
     * 드로어를 닫습니다. (modelValue를 false로 emit)
     */
    close() {
      this.$emit('update:modelValue', false)
    }
  }
}
</script>

<style scoped>
/* --- 드로어 배경 오버레이 --- */
.drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
}
/* --- 드로어 패널 (좌측 슬라이드인, 280px 고정 너비) --- */
.mobile-drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: 280px;
  height: 100vh;
  background: var(--color-sidebar-bg);
  z-index: 10000;
  overflow-y: auto;
  box-shadow: 4px 0 12px rgba(0, 0, 0, 0.3);
}
.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}
.drawer-close-btn {
  width: 44px;
  height: 44px;
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}
.drawer-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}
.drawer-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.drawer-submenu {
  background: rgba(255, 255, 255, 0.05);
}
.drawer-submenu li a {
  display: block;
  padding: 10px 16px 10px 32px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-decoration: none;
}
/* --- 배지가 붙은 항목만 좌우 정렬로 전환 (다른 서브메뉴의 레이아웃은 건드리지 않는다) --- */
.drawer-submenu li a.drawer-submenu-item--with-badge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.drawer-submenu-badge {
  flex-shrink: 0;
  min-width: 18px;
  padding: 0 6px;
  border-radius: 9999px;
  background: var(--color-danger, #C03A3A);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}
.drawer-submenu li a:active,
.drawer-submenu li a.drawer-submenu-item--active {
  color: white;
  background: rgba(59, 89, 152, 0.28);
  font-weight: 600;
  position: relative;
}
.drawer-submenu li a.drawer-submenu-item--active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 60%;
  background: var(--color-primary-500, #3B5998);
  border-radius: 0 2px 2px 0;
}
/* --- 드로어 슬라이드 트랜지션 --- */
.drawer-enter-active,
.drawer-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.drawer-enter-from,
.drawer-leave-to {
  transform: translateX(-100%);
}
.submenu-enter-active,
.submenu-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
}
.submenu-enter-from,
.submenu-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
